"use client";

import React, { useEffect, useState } from "react";
import {
  useChatNotificationBellDot,
  useNotificationBellNavigation,
} from "../useChatNotificationBell";

import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Notification/UserDataPushNotification/UserDataPushNotification";
import { cn } from "utils";
import { rootStore } from "@/src/store";
import { useIsPc } from "@/src/hooks/useIsPc";
import { useSnapshot } from "valtio/react";
import { useSwipeDismiss } from "../hooks/useSwipeDismiss";
import { useUserDataPushNotificationContext } from "./UserDataPushNotificationContext";

const I18n = I18nUtil.createScoped(Translations);

/**
 * wspush:all / userData 浮层（Figma `81033:18018` Alert Message，经 MCP get_design_context 对齐）
 * 仅非 PC 宽度（与 useIsPc 一致：视口宽度小于 964 为 H5）展示；大屏不渲染。
 */
export const UserDataPushNotification: React.FC = () => {
  const [isPc] = useIsPc();
  const { current, closeUserDataPushNotification, setIsHovering } =
    useUserDataPushNotificationContext();
  const [isVisible, setIsVisible] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const { acknowledge } = useChatNotificationBellDot(ownerInfo?.id);
  const { onBellClick } = useNotificationBellNavigation(ownerInfo?.id);
  const dismissNotification = closeUserDataPushNotification;
  const {
    cardRef,
    isDragging,
    shouldPreventClickRef,
    touchAction,
    contentStyle,
    handlers,
  } = useSwipeDismiss({
    onDismiss: dismissNotification,
  });

  useEffect(() => {
    if (current) {
      if (isVisible) {
        setIsRefreshing(true);
        setTimeout(() => {
          setIsRefreshing(false);
          setIsVisible(true);
        }, 300);
      } else {
        setIsVisible(true);
      }
    } else {
      setIsVisible(false);
      setIsRefreshing(false);
    }
  }, [current, isVisible]);

  if (!current || !isVisible || isPc) {
    return null;
  }

  return (
    <div
      className={cn(
        "pointer-events-none fixed inset-x-0 top-14 z-[9998]",
        "flex justify-center px-4 md:justify-end md:pr-10",
      )}
    >
      <div
        className="pointer-events-auto w-full max-w-[400px]"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div
          ref={cardRef}
          data-node-id="81033:18018"
          data-openreplay-label="UserDataPushNotification · card"
          className={cn(
            "flex w-full flex-col gap-1 overflow-hidden rounded-2 border border-solid p-3",
            "border-border_solid bg-background_solid_neutral_tertiary",
            "shadow-[3px_3px_16px_0px_rgba(0,0,0,0.25)]",
            "transition-all duration-300 ease-in-out",
            isRefreshing && "opacity-0 scale-95",
            !isRefreshing && "opacity-100 scale-100 animate-slide-down-right",
            isDragging && "cursor-grabbing",
          )}
          style={{ touchAction }}
          onPointerDown={handlers.onPointerDown}
          onPointerMove={handlers.onPointerMove}
          onPointerUp={handlers.onPointerUp}
          onPointerCancel={handlers.onPointerCancel}
        >
          <div style={contentStyle}>
            <div className="flex w-full shrink-0 items-start justify-between gap-2">
              <div className="min-h-0 min-w-0 flex-1">
                <p className="body_medium_m text-content_primary line-clamp-2 overflow-hidden break-words leading-[20px]">
                  {current.title}
                </p>
              </div>
              <div className="flex shrink-0">
                <button
                  type="button"
                  className="button_m text-content_brand_primary whitespace-nowrap leading-[20px] hover:underline"
                  onClick={() => {
                    if (shouldPreventClickRef.current) {
                      shouldPreventClickRef.current = false;
                      return;
                    }
                    if (current.onViewMoreClick) {
                      current.onViewMoreClick();
                      dismissNotification();
                      return;
                    }
                    acknowledge();
                    onBellClick();
                    dismissNotification();
                  }}
                >
                  {I18n.t("View More")}
                </button>
              </div>
            </div>
            {current.description ? (
              <p className="body_m text-content_tertiary w-full shrink-0 line-clamp-2 overflow-hidden break-words leading-[20px]">
                {current.description}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
